import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import TelegramAuth from '../components/common/TelegramAuth';
import { getCurrentUser } from '../services/telegram-auth';

const LoginPage = () => {
    const navigate = useNavigate();
    const [authError, setAuthError] = useState<string | null>(null);
    const [bookingIntent, setBookingIntent] = useState<any>(null);
    const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
    
    // Состояние для контроля загрузки видео
    const [videoLoaded, setVideoLoaded] = useState(false);
    const [videoError, setVideoError] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isMobile, setIsMobile] = useState(false);

    // Определяем тип устройства при загрузке и при изменении размера окна
    useEffect(() => {
        const checkIsMobile = () => {
            const mobile = window.innerWidth < 768 || 
                        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            setIsMobile(mobile);
            console.log('Устройство определено как:', mobile ? 'мобильное' : 'десктопное');
        };

        // Проверяем при загрузке
        checkIsMobile();

        // Слушаем изменение размера окна
        window.addEventListener('resize', checkIsMobile);

        return () => {
            window.removeEventListener('resize', checkIsMobile);
        };
    }, []);

    // Обработчик загрузки видео
    useEffect(() => {
        const videoElement = videoRef.current;
        if (videoElement) {
            const handleLoadedData = () => {
                console.log('Видео загружено успешно');
                setVideoLoaded(true);
                setVideoError(false);
            };

            const handleError = (e: Event) => {
                console.error('Ошибка загрузки видео:', e);
                setVideoError(true);
            };

            videoElement.addEventListener('loadeddata', handleLoadedData);
            videoElement.addEventListener('error', handleError);

            // Принудительная попытка воспроизведения
            videoElement.load();
            const playPromise = videoElement.play();
            if (playPromise !== undefined) {
                playPromise.catch(error => {
                    console.warn('Автовоспроизведение заблокировано:', error);
                });
            }

            return () => {
                videoElement.removeEventListener('loadeddata', handleLoadedData);
                videoElement.removeEventListener('error', handleError);
            };
        }
    }, [isMobile]);

    // Проверяем, авторизован ли пользователь и есть ли намерение бронирования
    useEffect(() => {
        // Проверяем наличие намерения бронирования
        const bookingIntentData = localStorage.getItem('bookingIntent');
        if (bookingIntentData) {
            try {
                setBookingIntent(JSON.parse(bookingIntentData));
            } catch (error) {
                console.error('Ошибка при парсинге данных бронирования:', error);
            }
        }

        // Проверяем наличие URL для перенаправления
        const authRedirectUrl = localStorage.getItem('authRedirectUrl');
        if (authRedirectUrl) {
            setRedirectUrl(authRedirectUrl);
        }

        const user = getCurrentUser();
        if (user) {
            // Если пользователь уже авторизован, перенаправляем
            let targetUrl = '/equipment';
            
            if (bookingIntent && bookingIntent.returnUrl) {
                // Если есть намерение бронирования, перенаправляем обратно к бронированию
                targetUrl = bookingIntent.returnUrl;
                // Очищаем намерение бронирования после перенаправления
                localStorage.removeItem('bookingIntent');
            } else if (redirectUrl) {
                // Если есть URL для перенаправления
                targetUrl = redirectUrl;
                localStorage.removeItem('authRedirectUrl');
            }
            
            navigate(targetUrl);
        }
    }, [navigate, bookingIntent, redirectUrl]);

    const handleAuth = (userData: any) => {
        if (!userData) {
            setAuthError('Ошибка авторизации. Пожалуйста, попробуйте снова.');
            return;
        }

        // В реальном приложении здесь должна быть логика сохранения токена сессии
        // и другие действия после успешной авторизации

        console.log('Пользователь успешно авторизован:', userData);

        // Определяем куда перенаправить пользователя
        let targetUrl = '/equipment';
        
        // Проверяем наличие намерения бронирования
        const bookingIntentData = localStorage.getItem('bookingIntent');
        if (bookingIntentData) {
            try {
                const intent = JSON.parse(bookingIntentData);
                if (intent.returnUrl) {
                    targetUrl = intent.returnUrl;
                }
                // Очищаем намерение бронирования после получения URL
                localStorage.removeItem('bookingIntent');
            } catch (error) {
                console.error('Ошибка при парсинге данных бронирования:', error);
            }
        } else {
            // Проверяем наличие URL для перенаправления
            const authRedirectUrl = localStorage.getItem('authRedirectUrl');
            if (authRedirectUrl) {
                targetUrl = authRedirectUrl;
                localStorage.removeItem('authRedirectUrl');
            }
        }

        // Перенаправляем после успешной авторизации
        navigate(targetUrl);
    };

    // Статичное фоновое изображение для запасного варианта
    const fallbackImage = "https://images.unsplash.com/photo-1581094794329-c8112a89f47f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80";

    return (
        <div className="fixed inset-0 overflow-hidden">
            {/* Фоновое видео на всю страницу */}
            <div className="absolute top-0 left-0 w-full h-full z-0 bg-apple-800">
                {/* Показываем статичное изображение только при ошибке загрузки видео */}
                {videoError && (
                    <img
                        src={fallbackImage}
                        alt="Техника для аренды"
                        className="w-full h-full object-cover opacity-70"
                    />
                )}
                
                <video
                    ref={videoRef}
                    className={`w-full h-full object-cover transition-opacity duration-700 ${videoLoaded ? 'opacity-70' : 'opacity-0'} ${videoError ? 'hidden' : ''}`}
                    autoPlay
                    muted
                    loop
                    playsInline={true}
                    preload="auto"
                >
                    {isMobile ? (
                        <>
                            <source src="/videos/bg-rental_2.mp4" type="video/mp4" />
                            <source src="/videos/bg-rental_2.webm" type="video/webm" />
                        </>
                    ) : (
                        <>
                            <source src="/videos/bg-rental_2.webm" type="video/webm" />
                            <source src="/videos/bg-rental_2.mp4" type="video/mp4" />
                        </>
                    )}
                </video>
            </div>

            {/* Наложение для улучшения читаемости */}
            <div className="absolute top-0 left-0 w-full h-full z-[1] bg-gradient-to-r from-apple-900/80 to-apple-800/50"></div>

            {/* Контент страницы */}
            <div className="absolute inset-0 z-[2] flex items-center justify-center p-4">
                <div className="w-full max-w-md">
                    <div className="card bg-white/95 backdrop-blur-sm shadow-xl">
                        <div className="card-body p-6 rounded-lg">
                            <h2 className="text-2xl font-bold text-center w-full mb-6 text-gray-800">
                                {bookingIntent || redirectUrl ? 'Вход для продолжения' : 'Вход в систему бронирования техники'}
                            </h2>

                            {authError && (
                                <div className="alert alert-error mb-4 p-4 bg-red-50 text-red-800 rounded-lg" role="alert">
                                    {authError}
                                </div>
                            )}

                            <div className="divider mb-6">Войти через Telegram</div>

                            <div className="flex justify-center">
                                <TelegramAuth onAuth={handleAuth} />
                            </div>

                            <p className="text-sm text-center mt-6 text-gray-600">
                                Нажимая кнопку входа через Telegram, вы соглашаетесь с правилами использования сервиса.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPage; 